import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Box } from "@mui/material";
import { useEffect, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { CheckboxTextField } from "@/controls/CheckboxTextField";
import { ConfirmMessageModal } from "@/controls/modals/ConfirmMessageModal";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	BREAK_RULE_INITIAL_VALUES,
	type BreakRule,
	type BreakRuleImpact,
	type BreakRulePreset,
} from "@/shared/models/BreakRule";
import { useBreakRules } from "../hooks/useBreakRules";
import { useBreakRuleActions } from "../hooks/useBreakRuleActions";
import type { BreakRuleModal } from "../hooks/useTableData";
import { type BreakRuleForm, toForm, toRule } from "../utils/breakRuleForm";
import { BreakRuleFields } from "./BreakRuleFields";

type Props = {
	breakRule: BreakRule | null;
	preset?: BreakRulePreset | null;
	modal: BreakRuleModal;
	onHide: () => void;
};
const TITLES = { create: "CREATE", update: "EDIT", delete: "DELETE" };
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const updateMessage = (
	{ Untouched, Adjusted }: BreakRuleImpact,
	rule: BreakRule,
	previous: BreakRule | null,
) => {
	const timeChanged =
		rule.OffsetMinutes !== previous?.OffsetMinutes ||
		rule.DurationMinutes !== previous?.DurationMinutes;
	const spellChanged = rule.IsSpell !== previous?.IsSpell;
	const changes = [
		timeChanged ? "schedule" : "",
		spellChanged ? "spell setting" : "",
	]
		.filter(Boolean)
		.join(" and ");
	if (!changes) return "Update the break label?";
	const adjusted = Adjusted
		? ` The timing and spell of ${plural(Adjusted, "break")} adjusted by hand will stay as they are.`
		: "";
	return `${plural(Untouched, "upcoming scheduled break")} will follow the updated ${changes}.${timeChanged ? " Breaks that no longer fit their shift will be removed." : ""}${adjusted} Continue?`;
};

export const UpsertDeleteModal = ({
	breakRule,
	preset = null,
	modal,
	onHide,
}: Props) => {
	const isCreate = modal === "create";
	const isDelete = modal === "delete";
	const form = useForm<BreakRuleForm>({ defaultValues: toForm(null) });
	const { reset, handleSubmit } = form;
	const { data } = useBreakRules();
	const actions = useBreakRuleActions(modal, onHide);
	const { pending, includeAdjusted, setIncludeAdjusted } = actions;
	const close = () => {
		if (!actions.isBusy) onHide();
	};
	useEffect(() => {
		if (modal)
			reset(
				toForm(
					isCreate
						? preset
							? { ...BREAK_RULE_INITIAL_VALUES, ...preset }
							: null
						: breakRule,
				),
			);
	}, [modal, breakRule, preset, reset, isCreate]);
	const submit = (event: FormEvent<HTMLFormElement>) => {
		if (isDelete && breakRule) {
			event.preventDefault();
			actions.submit(breakRule);
		} else {
			void handleSubmit((values) =>
				actions.submit(
					toRule(values, isCreate ? null : (breakRule?.BreakRuleId ?? null)),
				),
			)(event);
		}
	};
	return (
		<>
			{modal && (
				<HelmetDexteel title={`${TITLES[modal]} Break - Break Schedules`} />
			)}
			<MesfModal
				id="modal-break-rule"
				title={modal ? `${TITLES[modal]} BREAK` : ""}
				open={!!modal}
				handleClose={close}
				maxWidth="sm"
			>
				<form onSubmit={submit} noValidate>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<BreakRuleFields
							form={form}
							lines={data?.lines ?? []}
							rules={data?.rules ?? []}
							editingRuleId={isCreate ? null : (breakRule?.BreakRuleId ?? null)}
							locked={!isCreate || preset != null}
							isDelete={isDelete}
							disabled={actions.isBusy}
						/>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={actions.isBusy}
							onHide={close}
							buttonLabel={isDelete ? "Delete" : "Save"}
							buttonColor={isDelete ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ConfirmMessageModal
				show={pending != null}
				subject={`${isDelete ? "Delete" : "Update"} ${pending?.rule.Label ?? ""} — ${breakRule?.AssetName ?? ""}, ${pending?.rule.ShiftHours ?? ""}h shifts`}
				confirmColor={isDelete ? "secondary" : "primary"}
				message={
					!pending
						? ""
						: isDelete
							? `${plural(pending.impact.Untouched, "upcoming scheduled break")} will be removed from the calendar. Continue?`
							: updateMessage(pending.impact, pending.rule, breakRule)
				}
				onHide={actions.confirm}
			>
				{isDelete && (pending?.impact.Adjusted ?? 0) > 0 && (
					<Box sx={{ mt: 2 }}>
						<CheckboxTextField
							label={`Also remove the ${plural(pending?.impact.Adjusted ?? 0, "break")} adjusted by hand on the calendar`}
							checked={includeAdjusted}
							onChange={setIncludeAdjusted}
						/>
					</Box>
				)}
			</ConfirmMessageModal>
			<NotificationSnackBar
				message={actions.notificationMessage}
				onHide={actions.clearNotification}
			/>
			<ErrorModal error={actions.error} onHide={actions.clearError} />
		</>
	);
};
